// HDB lease-decay engine. Remaining lease drives CPF usage, LTV, loan tenure and
// resale liquidity — two same-priced flats with different remaining leases are
// NOT comparable. Pure functions, browser-only, no I/O. Decision support, not
// advice: the CPF/HDB rules below are the documented general case, encoded as
// tunable constants — verify against HDB/CPF before acting.
//
// The core rule (simplified): CPF and the full loan-to-value apply when the
// flat's remaining lease covers the *youngest buyer to age 95*. When it doesn't
// (but is still ≥ 20 years), CPF usage and LTV are pro-rated by how much of that
// "to-95" span the lease covers — HDB's Valuation-Limit pro-ration in spirit.
// Below ~20 years, CPF cannot be used and financing effectively dries up.

import { at, CPF_LEASE_RULES } from "../policy/policy";

const cpfLease = at(CPF_LEASE_RULES).value;
export const LEASE_RULES = {
  coverToAge: cpfLease.coverToAge, // CPF rule (policy.ts): full terms if the lease covers the youngest buyer to 95
  cpfMinLeaseYears: cpfLease.minLeaseYears, // below this, no CPF may be used (UNVERIFIED in policy.ts)
  liquidityWarnYears: 60, // below this, the resale buyer pool/financing narrows
  liquidityHighRiskYears: 45, // below this, financing frictions are pronounced
  hdbLoanTenureMax: 25, // HDB concessionary-loan tenure ceiling
};

export type LeaseRisk = "low" | "moderate" | "elevated" | "high";

export interface LeaseFinance {
  remainingLease: number; // years, as supplied
  coversToAge: boolean; // lease covers the youngest buyer to age 95
  cpfFraction: number; // 0..1 of CPF OA usable toward this flat
  ltvFactor: number; // 0..1 multiplier on the standard LTV
  maxTenureCap: number; // years — hard cap on loan tenure from the lease
  risk: LeaseRisk;
  note: string; // one-line, human-readable rationale
}

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/**
 * Financing implications of a flat's remaining lease for a given youngest-buyer
 * age. `age` is the youngest applicant (proxied by the profile age when only one
 * is known) because HDB measures the "to-95" span against the youngest owner.
 */
export function hdbLeaseFinance(remainingLease: number, age: number): LeaseFinance {
  const R = LEASE_RULES;
  const span = Math.max(1, R.coverToAge - age); // years the lease must cover
  const coversToAge = remainingLease >= span;

  let cpfFraction: number;
  let ltvFactor: number;
  if (remainingLease < R.cpfMinLeaseYears) {
    // Too short for CPF; banks/HDB won't extend a normal housing loan either.
    cpfFraction = 0;
    ltvFactor = 0;
  } else if (coversToAge) {
    cpfFraction = 1;
    ltvFactor = 1;
  } else {
    // Pro-rate both by how much of the to-95 span the lease covers.
    const frac = clamp01(remainingLease / span);
    cpfFraction = frac;
    ltvFactor = frac;
  }

  // A loan cannot outlast the lease, and HDB caps tenure at 25 years regardless.
  const maxTenureCap = Math.max(0, Math.min(R.hdbLoanTenureMax, Math.floor(remainingLease)));

  const risk = leaseRisk(remainingLease);
  return {
    remainingLease,
    coversToAge,
    cpfFraction,
    ltvFactor,
    maxTenureCap,
    risk,
    note: leaseNote({ remainingLease, coversToAge, cpfFraction, ltvFactor, risk }),
  };
}

export function leaseRisk(remainingLease: number): LeaseRisk {
  const R = LEASE_RULES;
  if (remainingLease < R.cpfMinLeaseYears) return "high";
  if (remainingLease < R.liquidityHighRiskYears) return "high";
  if (remainingLease < R.liquidityWarnYears) return "elevated";
  if (remainingLease < 80) return "moderate";
  return "low";
}

function leaseNote(f: {
  remainingLease: number;
  coversToAge: boolean;
  cpfFraction: number;
  ltvFactor: number;
  risk: LeaseRisk;
}): string {
  const y = Math.round(f.remainingLease);
  if (f.remainingLease < LEASE_RULES.cpfMinLeaseYears) {
    return `~${y} yrs left — under ${LEASE_RULES.cpfMinLeaseYears}y: CPF can't be used and a housing loan is unlikely, so this is effectively a cash purchase with a thin resale pool.`;
  }
  if (f.coversToAge) {
    return `~${y} yrs left — covers the youngest buyer to age ${LEASE_RULES.coverToAge}, so full CPF and LTV apply.`;
  }
  const pct = Math.round(f.cpfFraction * 100);
  return `~${y} yrs left — doesn't cover the youngest buyer to age ${LEASE_RULES.coverToAge}, so CPF usage and LTV are pro-rated to about ${pct}%. More cash upfront, and a narrower resale pool later.`;
}

export const RISK_LABEL: Record<LeaseRisk, string> = {
  low: "Lease healthy",
  moderate: "Lease ageing",
  elevated: "Lease decay — watch",
  high: "Short lease — caution",
};
